import React from "react";
import { Play, ArrowLeft, Loader2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { AvatarSVG } from "@/lib/avatarBuilder";
import { Modal } from "@/components/gameplay/parts";
import FitText from "@/components/FitText";

/**
 * Pre-game splash shown while phase === "ready":
 * - Avatar preview
 * - Game name + level label
 * - Lives info banner (time-based modes only)
 * - Competition / Eliminatória summary banners (when applicable)
 * - Back + Start + optional DEMO buttons
 *
 * The parent owns navigation/start callbacks. Everything else is pure
 * display logic derived from `session` + `mode`.
 */
export default function ReadyModal({ session, mode, avatarCfg, levelLabel, onBack, onStart, onDemo, starting = false }) {
  const { t } = useI18n();
  if (!session) return null;

  const showLivesInfo = mode === "training" || mode === "competition" || mode === "eliminatoria";
  const lives = Math.max(1, Number(session?.game?.lives ?? 1));
  const penalty = Math.max(0, Number(session?.game?.time_penalty_seconds ?? 5));

  return (
    <Modal>
      <div className="mb-3 flex justify-center">
        <AvatarSVG cfg={avatarCfg} size={80} />
      </div>
      <h2 className="text-2xl md:text-3xl font-[Fredoka] font-bold text-slate-900 mb-2" data-testid="ready-title">
        {t("ready_title")}
      </h2>
      <FitText
        as="p"
        maxPx={16}
        minPx={10}
        className="text-slate-600 mb-3"
        title={`${session.game.name} · ${levelLabel}`}
      >
        {session.game.name} · {levelLabel}
      </FitText>

      {showLivesInfo && lives > 1 && (
        <div className="mb-5 mx-auto max-w-sm rounded-2xl bg-emerald-50 border-2 border-emerald-300 px-3 py-2" data-testid="ready-lives-info">
          <div className="text-sm font-[Fredoka] font-extrabold text-emerald-700">
            {t("lives_available", { n: lives })}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            {t("wrong_costs", { n: penalty })}
          </div>
        </div>
      )}

      {mode === "competition" && session?.game?.max_attempts != null && (() => {
        const maxAttempts = Number(session.game.max_attempts || 0);
        // Backend now derives `attempts_available` from the count of FINISHED
        // attempts only — session creation does NOT burn a slot. So this
        // value already reflects "how many attempts the player can still play
        // (including the current one they're about to start)".
        const available = Math.max(0, Math.min(maxAttempts, Number(session.attempts_available ?? 0)));
        const used = Math.max(0, maxAttempts - available);
        return (
          <div className="mb-5 mx-auto max-w-sm rounded-2xl bg-amber-50 border-2 border-amber-200 p-3 text-left" data-testid="ready-competition-summary">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 mb-1">{t("ready_competition_header")}</div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-white py-1.5 px-1 border border-amber-100">
                <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_competition_used")}</div>
                <div className="text-sm font-bold text-amber-600" data-testid="ready-competition-used">{used}/{maxAttempts}</div>
              </div>
              <div className="rounded-lg bg-white py-1.5 px-1 border border-amber-100">
                <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_competition_left")}</div>
                <div className="text-sm font-bold text-emerald-600" data-testid="ready-competition-left">{available}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {mode === "eliminatoria" && session?.eliminatoria && (() => {
        const elim = session.eliminatoria;
        const elimMode = elim.mode || "target_time";
        const left = elim.attempts_left ?? elim.max_attempts;

        // Wins-vs-Losses — no target-time; show running scoreboard instead.
        if (elimMode === "wins_vs_losses") {
          // Prefer group-aware payload: `wvl_progress` reflects the current
          // attempt (group). If missing (older sessions), fall back to the
          // flat elim.* fields.
          const wvl = elim.wvl_progress || null;
          const wins = wvl?.wins_in_attempt ?? elim.wins_count ?? 0;
          const losses = wvl?.losses_in_attempt ?? elim.losses_count ?? 0;
          const winsRequired = wvl?.wins_required ?? elim.wins_required ?? 3;
          const totalGames = wvl?.total_games ?? elim.total_games ?? 5;
          const gamesLeft = Math.max(0, totalGames - (wins + losses));
          const maxAttempts = Number(wvl?.max_attempts ?? elim.max_attempts ?? 3);
          const attemptNumber = Number(wvl?.attempt_number ?? elim.attempts_used ?? 1) || 1;
          return (
            <div className="mb-5 mx-auto max-w-sm rounded-2xl bg-violet-50 border-2 border-violet-200 p-3 text-left" data-testid="ready-elim-summary">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-violet-700 mb-1">
                {t("ready_elim_header_wvl")}
              </div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-bold text-slate-900 leading-snug flex-1 min-w-0 truncate">{elim.name}</div>
                <div
                  className="text-[11px] font-[Fredoka] font-extrabold text-violet-700 bg-violet-100 border border-violet-200 rounded-full px-2.5 py-0.5 shrink-0"
                  data-testid="ready-elim-attempt-number"
                >
                  {t("ready_elim_attempt_x_of_y", { current: attemptNumber, total: maxAttempts })}
                </div>
              </div>
              <div className="text-xs text-slate-700 mb-2 leading-snug" data-testid="ready-elim-objective">
                <span className="font-bold text-slate-500">{t("elim_card_objective_label")}:</span>{" "}
                {t("elim_obj_wvl", { wins: winsRequired, total: totalGames })}
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-bold text-slate-500">{t("elim_card_attempts_total_label")}:</span> {maxAttempts}
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-bold text-slate-500">{t("elim_card_attempts_avail_label")}:</span>{" "}
                <span className={`font-bold ${Number(left) > 0 ? "text-emerald-600" : "text-rose-600"}`}>{left}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                  <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_elim_wins")}</div>
                  <div className="text-sm font-bold text-emerald-600" data-testid="ready-elim-wins">
                    {wins}
                  </div>
                </div>
                <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                  <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_elim_losses")}</div>
                  <div className="text-sm font-bold text-rose-600" data-testid="ready-elim-losses">
                    {losses}
                  </div>
                </div>
                <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                  <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_elim_games_left")}</div>
                  <div className="text-sm font-bold text-amber-600" data-testid="ready-elim-left">
                    {gamesLeft}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Vencer Robot — no "target time" concept; only show attempts.
        if (elimMode === "vencer_robot") {
          return (
            <div className="mb-5 mx-auto max-w-sm rounded-2xl bg-violet-50 border-2 border-violet-200 p-3 text-left" data-testid="ready-elim-summary">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-violet-700 mb-1">
                {t("ready_elim_header_robot")}
              </div>
              <div className="text-sm font-bold text-slate-900 leading-snug mb-2">{elim.name}</div>
              {elim.target_robot && (
                <div className="flex items-center gap-2 bg-white border border-violet-100 rounded-xl px-2.5 py-2 mb-2" data-testid="ready-elim-robot">
                  {elim.target_robot.image_url && (
                    <img src={elim.target_robot.image_url} alt={elim.target_robot.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  )}
                  <div className="min-w-0 flex-1 text-left">
                    <div className="text-xs text-slate-700">
                      <span className="font-bold text-slate-500">{t("elim_card_objective_label")}:</span>{" "}
                      {t("elim_obj_robot", { name: elim.target_robot.name })}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {elim.target_robot.alcunha ? `"${elim.target_robot.alcunha}" · ` : ""}{t("elim_card_mental")}: <strong>{elim.target_robot.mental_strength}/10</strong>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                  <div className="text-[9px] uppercase font-bold text-slate-500">{t("elim_card_attempts_total_label")}</div>
                  <div className="text-sm font-bold text-slate-700" data-testid="ready-elim-total">{elim.max_attempts}</div>
                </div>
                <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                  <div className="text-[9px] uppercase font-bold text-slate-500">{t("elim_card_attempts_avail_label")}</div>
                  <div className={`text-sm font-bold ${Number(left) > 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="ready-elim-left">{left}</div>
                </div>
              </div>
            </div>
          );
        }

        // avg_time OR target_time — both time-based. Show a clear objective
        // line plus total/available attempts, consistent with the lobby card.
        const isAvg = elimMode === "avg_time";
        const tt = Number(elim.target_time || 0).toFixed(0);
        const objective = isAvg
          ? t("elim_obj_avg", { target: tt, games: elim.total_games ?? 5 })
          : t("elim_obj_target", { seconds: tt });
        return (
          <div className="mb-5 mx-auto max-w-sm rounded-2xl bg-violet-50 border-2 border-violet-200 p-3 text-left" data-testid="ready-elim-summary">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-violet-700 mb-1">{isAvg ? t("elim_card_mode_avg") : t("ready_elim_header")}</div>
            <div className="text-sm font-bold text-slate-900 leading-snug mb-1">{elim.name}</div>
            <div className="text-xs text-slate-700 mb-2 leading-snug" data-testid="ready-elim-objective">
              <span className="font-bold text-slate-500">{t("elim_card_objective_label")}:</span> {objective}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                <div className="text-[9px] uppercase font-bold text-slate-500">{t("ready_elim_target")}</div>
                <div className="text-sm font-bold text-violet-700" data-testid="ready-elim-target">
                  {tt}s
                </div>
              </div>
              <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                <div className="text-[9px] uppercase font-bold text-slate-500">{t("elim_card_attempts_total_label")}</div>
                <div className="text-sm font-bold text-slate-700" data-testid="ready-elim-total">{elim.max_attempts}</div>
              </div>
              <div className="rounded-lg bg-white py-1.5 px-1 border border-violet-100">
                <div className="text-[9px] uppercase font-bold text-slate-500">{t("elim_card_attempts_avail_label")}</div>
                <div className={`text-sm font-bold ${Number(left) > 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="ready-elim-left">{left}</div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button
          data-testid="btn-ready-back"
          onClick={onBack}
          variant="outline"
          className="rounded-full font-[Fredoka] font-bold text-base md:text-lg px-6 py-5 border-2 border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> {t("back_btn")}
        </Button>
        {onDemo && (
          <Button
            data-testid="btn-ready-demo"
            onClick={onDemo}
            disabled={starting}
            className="bg-violet-500 hover:bg-violet-600 text-white font-[Fredoka] font-bold text-base md:text-lg px-6 py-5 rounded-full border-b-4 border-violet-700 active:border-b-0 active:translate-y-1 disabled:opacity-70"
          >
            <GraduationCap className="w-5 h-5 mr-2" /> {t("demo_btn") || "DEMO"}
          </Button>
        )}
        <Button
          data-testid="btn-iniciar-jogo"
          onClick={onStart}
          disabled={starting}
          className="bg-amber-400 hover:bg-amber-500 text-amber-950 font-[Fredoka] font-bold text-lg md:text-xl px-8 py-5 rounded-full border-b-4 border-amber-600 active:border-b-0 active:translate-y-1 disabled:opacity-80"
        >
          {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2" />} {t("start_game")}
        </Button>
      </div>
    </Modal>
  );
}
